import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, arrayUnion } from 'firebase/firestore';
import type { DocumentData, Firestore } from 'firebase/firestore';
import type { StakkoAppId, StakkoAuthUser, StakkoProfile } from '../types';

function toProfile(uid: string, data: DocumentData): StakkoProfile {
  return {
    uid,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    emailLower: typeof data.emailLower === 'string' ? data.emailLower : '',
    photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
    school: typeof data.school === 'string' ? data.school : null,
    createdAt: data.createdAt,
    lastSeenAt: data.lastSeenAt,
    apps: Array.isArray(data.apps) ? (data.apps as StakkoAppId[]) : [],
  };
}

/**
 * Zajistí, že uživatel má Stakko profil. Je idempotentní – bezpečné volat při
 * každém přihlášení i v React StrictMode.
 */
export async function ensureStakkoProfile(
  db: Firestore,
  user: StakkoAuthUser,
  appId: StakkoAppId,
): Promise<void> {
  const ref = doc(db, 'vibiz_profiles', user.uid);
  const emailLower = (user.email ?? '').toLowerCase();
  const displayName =
    user.displayName || (emailLower ? emailLower.split('@')[0] : 'Student');

  const payload = {
    uid: user.uid,
    displayName,
    emailLower,
    photoURL: user.photoURL ?? null,
    lastSeenAt: serverTimestamp(),
    apps: arrayUnion(appId),
  };

  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, { ...payload, createdAt: serverTimestamp() });
    return;
  }

  await setDoc(ref, payload, { merge: true });
}

export function subscribeStakkoProfile(
  db: Firestore,
  uid: string,
  onData: (profile: StakkoProfile | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'vibiz_profiles', uid),
    (snap) => {
      onData(snap.exists() ? toProfile(uid, snap.data()) : null);
    },
    (error) => {
      console.warn('[stakko] profil se nepodařilo načíst', error);
      onData(null);
    },
  );
}
